import * as React from "react";
import {translate} from "react-i18next";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";

import {Segment, Table} from "semantic-ui-react";

// services
import {RoleDto, PrivilegeDto} from "../../../services/Client";

import DeleteButton from "../../../components/DeleteButton";
import PrivilegeSelection from "./PrivilegeSelection";

interface IOptionalControls {
    /** Check whether a given DTO has the links to update itself. */
    canUpdateRole?: (dto: RoleDto) => boolean;

    /** Check whether a given DTO has the links to delete itself. */
    canDeleteRole?: (dto: RoleDto) => boolean;
}
export interface IRolesTableProps extends ITranslatedComponent {
    /** Role objects to display in the table. */
    roles: RoleDto[];

    /** All available privileges. */
    privileges: PrivilegeDto[];

    /** Whether or not the control is waiting for a response from the back end. */
    loading: boolean;

    /** Callbacks to check whether a given DTO has the links for optional features. */
    optionalControls?: IOptionalControls;

    /** Callback for when the table want's to update a role. */
    onUpdateRole?: (oldRole: RoleDto, newRole: RoleDto) => any;

    /** Callback for when the table want's to delete a role. */
    onDeleteRole?: (role: RoleDto) => any;
}

/**
 * A table-like component that displays a collection of RoleDtos in a
 * structured way and offers actions to manipulate said roles given
 * the necessary privileges/links.
 *
 * The component doesn't manipulate data itself but relies on the client
 * to provide the necessary call backs to update and delete a role.
 */
export class RolesTable extends React.Component<IRolesTableProps> {
    hasLinkForPatchRole = (dto: RoleDto): boolean => (
        dto.links ? "PATCH_role" in dto.links : false
    )

    hasLinkForDeleteRole = (dto: RoleDto): boolean => (
        dto.links ? "DELETE_role" in dto.links : false
    )

    renderHeader = (): JSX.Element => (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>{this.props.t("NAME")}</Table.HeaderCell>
                <Table.HeaderCell>{this.props.t("PRIVILEGES")}</Table.HeaderCell>
                <Table.HeaderCell>{this.props.t("ACTIONS")}</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    )

    renderRolename = (dto: RoleDto): JSX.Element => (
        <Table.Cell>{dto.name}</Table.Cell>
    )

    renderPrivileges = (dto: RoleDto): JSX.Element => (
        <Table.Cell className="privileges">
            {
                this.props.onUpdateRole &&
                    this.hasLinkForPatchRole(dto) ? (
                        <PrivilegeSelection
                            placeholder={this.props.t("SELECT_PRIVILEGES")}
                            privileges={this.props.privileges}
                            values={dto.privileges && dto.privileges}
                            onChange={(privileges: PrivilegeDto[]) => {
                                if (this.props.onUpdateRole) {
                                    const updatedRole = Object.assign({}, dto, {privileges: privileges});
                                    this.props.onUpdateRole(dto, updatedRole);
                                }
                            }}
                        />
                    ) : (
                        dto.privileges &&
                        dto.privileges.map((privilege: PrivilegeDto) => (privilege.identifier)).join(", ")
                    )
            }
        </Table.Cell>
    )

    renderActions = (dto: RoleDto): JSX.Element => (
        <Table.Cell>
            {
                this.props.onDeleteRole &&
                this.hasLinkForDeleteRole(dto) &&
                <DeleteButton
                    label={this.props.t("DELETE_ROLE")}
                    confirmLabel={this.props.t("CONFIRM")}
                    disabled={this.props.loading}
                    requireConfirmation={true}
                    onClick={() => {
                        if (this.props.onDeleteRole) {
                            this.props.onDeleteRole(dto);
                        }
                    }}
                />
            }
        </Table.Cell>
    )

    renderRows = (dtos: RoleDto[]): JSX.Element[] => (
        dtos.map((dto, index) => (
            <Table.Row key={index}>
                {this.renderRolename(dto)}
                {this.renderPrivileges(dto)}
                {this.renderActions(dto)}
            </Table.Row>
        ))
    )

    renderTable = (dtos: RoleDto[]): JSX.Element => (
        <Table>
            {this.renderHeader()}
            <Table.Body>
                {this.renderRows(dtos)}
            </Table.Body>
        </Table>
    )

    render() {
        return (
            <Segment loading={this.props.loading}>
                {this.renderTable(this.props.roles)}
            </Segment>
        );
    }
}

export default translate("ADMIN_VIEW")(RolesTable);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/RolesTable.tsx